import React, { useMemo, useState } from 'react'
import {
  Select,
  SELECT_DEMO_OPTIONS,
  SELECT_PREVIEW_STATE_LABELS,
  SELECT_PREVIEW_STATES,
  type SelectPreviewState,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './SelectShowcase.css'

const LIVE_PREVIEW_OPTIONS = [
  { value: 'dr-smith', label: 'Dr. Ava Smith' },
  { value: 'dr-jones', label: 'Dr. Marcus Jones' },
  { value: 'dr-lee', label: 'Dr. Priya Lee' },
  { value: 'dr-chen', label: 'Dr. Daniel Chen' },
  { value: 'dr-patel', label: 'Dr. Sofia Patel' },
]

const SelectShowcase: React.FC = () => {
  const [providerValue, setProviderValue] = useState<string | string[]>('')
  const [timeValue, setTimeValue] = useState<string | string[]>('')
  const [label, setLabel] = useState('Provider')
  const [disabled, setDisabled] = useState(false)
  const [optional, setOptional] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [multiple, setMultiple] = useState(false)
  const [leadingIcon, setLeadingIcon] = useState(false)
  const [error, setError] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [helperText, setHelperText] = useState('')
  const [highlightedState, setHighlightedState] =
    useState<SelectPreviewState>('default')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'select-highlight-state',
            label: 'Highlighted state',
            control: (
              <select
                id="select-highlight-state"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(event.target.value as SelectPreviewState)
                }
              >
                {SELECT_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {SELECT_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'select-label',
            label: 'Provider label',
            control: (
              <input
                id="select-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
          {
            id: 'select-helper-text',
            label: 'Helper text',
            control: (
              <input
                id="select-helper-text"
                className="ui-field-control"
                type="text"
                value={helperText}
                placeholder="Optional helper text"
                onChange={(event) => setHelperText(event.target.value)}
              />
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'select-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="select-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'select-optional',
            label: 'Optional',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="select-optional"
                  type="checkbox"
                  checked={optional}
                  onChange={(event) => setOptional(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'select-tooltip',
            label: 'Tooltip',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="select-tooltip"
                  type="checkbox"
                  checked={showTooltip}
                  onChange={(event) => setShowTooltip(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'select-multiple',
            label: 'Multiple',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="select-multiple"
                  type="checkbox"
                  checked={multiple}
                  onChange={(event) => {
                    setMultiple(event.target.checked)
                    setProviderValue(event.target.checked ? [] : '')
                    setTimeValue(event.target.checked ? [] : '')
                  }}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'select-leading-icon',
            label: 'Leading icon',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="select-leading-icon"
                  type="checkbox"
                  checked={leadingIcon}
                  onChange={(event) => setLeadingIcon(event.target.checked)}
                />
                <span>Clock icon on time select</span>
              </label>
            ),
          },
          {
            id: 'select-error',
            label: 'Error',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="select-error"
                  type="checkbox"
                  checked={error}
                  onChange={(event) => setError(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'select-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="select-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [
      darkMode,
      disabled,
      error,
      helperText,
      highlightedState,
      label,
      leadingIcon,
      multiple,
      optional,
      showTooltip,
    ],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Select"
        description="Dropdown field with label, optional tooltip, helper text, leading icon, single and multi-select menus."
        stageClassName="ui-preview__stage--wide select-showcase__stage-wrap"
      >
        <div
          className={`select-showcase__sheet${
            darkMode ? ' select-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Two common use cases side by side: provider selection and
              preferred time slots. Property controls apply to both examples.
            </p>
            <div className="select-showcase__live-grid">
              <Select
                label={label}
                placeholder="Select provider"
                value={providerValue}
                options={LIVE_PREVIEW_OPTIONS}
                disabled={disabled}
                optional={optional}
                showTooltip={showTooltip}
                multiple={multiple}
                darkMode={darkMode}
                error={error}
                helperText={helperText || undefined}
                onChange={setProviderValue}
              />
              <Select
                label="Preferred time"
                placeholder="Select"
                value={timeValue}
                options={SELECT_DEMO_OPTIONS}
                disabled={disabled}
                optional={optional}
                showTooltip={showTooltip}
                multiple={multiple}
                darkMode={darkMode}
                leadingIcon={leadingIcon ? 'clock' : undefined}
                error={error}
                helperText={helperText || undefined}
                onChange={setTimeValue}
              />
            </div>
          </section>

          <section className="select-showcase__matrix">
            {SELECT_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`select-showcase__row${
                  previewState === highlightedState
                    ? ' select-showcase__row--selected'
                    : ''
                }${
                  previewState === 'expanded' ||
                  previewState === 'multiple-options'
                    ? ' select-showcase__row--expanded'
                    : ''
                }${darkMode ? ' select-showcase__row--dark' : ''}`}
              >
                <h4 className="select-showcase__label">
                  {SELECT_PREVIEW_STATE_LABELS[previewState]}
                </h4>
                <Select previewState={previewState} darkMode={darkMode} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Select" sections={propertySections} />
    </>
  )
}

export default SelectShowcase
